import {
  respondToPortalRenewal,
  useListPortalNotices,
  useListPortalRenewals,
  type PortalRenewalOut,
} from '@myrentalmate/api-client';
import { Alert, Button, Chip, Paper, Stack, TextField, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { okData, okItems } from '../../lib/apiData';
import { apiErrorMessage } from '../../lib/apiError';
import { formatDate, formatDateTime, formatMoney } from '../../lib/format';
import { NOTICE_KIND, RENEWAL_STATUS } from '../compliance/labels';
import { NotLinked } from './NotLinked';
import { usePortalHome } from './usePortalHome';

function RenewalCard({ offer }: { offer: PortalRenewalOut }) {
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function respond(accept: boolean) {
    const question = accept ? 'Accept this offer?' : 'Decline this offer?';
    if (!window.confirm(question)) return;
    setBusy(true);
    setError(null);
    try {
      await respondToPortalRenewal(offer.id, { accept, note: note.trim() || null });
      await queryClient.invalidateQueries();
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  const open = offer.status === 'sent' && !offer.expired;
  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Lease Renewal Offer: {offer.place}
          </Typography>
          {offer.expired ? (
            <Chip size="small" label="Expired" />
          ) : (
            <Chip
              size="small"
              color={RENEWAL_STATUS[offer.status].color}
              label={open ? 'Waiting for You' : RENEWAL_STATUS[offer.status].label}
            />
          )}
        </Stack>
        <Typography>
          Monthly rent: {formatMoney(offer.current_rent)} now, {formatMoney(offer.new_rent)} under
          the new term.
        </Typography>
        <Typography>
          Lease ends:{' '}
          {offer.new_end_date ? formatDate(offer.new_end_date) : 'Month to month from then on'}.
        </Typography>
        {offer.message && <Typography sx={{ whiteSpace: 'pre-line' }}>{offer.message}</Typography>}
        {error && <Alert severity="error">{error}</Alert>}
        {open && (
          <>
            <Typography variant="body2" color="text.secondary">
              Open until {formatDate(offer.offer_expires_on)}.
            </Typography>
            <TextField
              label="Note to Your Property Manager"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              slotProps={{ htmlInput: { maxLength: 500 } }}
            />
            <Stack direction="row" spacing={1}>
              <Button variant="contained" disabled={busy} onClick={() => void respond(true)}>
                Accept
              </Button>
              <Button disabled={busy} onClick={() => void respond(false)}>
                Decline
              </Button>
            </Stack>
          </>
        )}
      </Stack>
    </Paper>
  );
}

export function PortalNoticesPage() {
  const { home, notLinked } = usePortalHome();
  const notices = okItems(useListPortalNotices({ query: { enabled: !!home } }).data);
  const offers = okData(useListPortalRenewals({ query: { enabled: !!home } }).data)?.items ?? [];
  if (notLinked) return <NotLinked />;
  if (!home) return null;
  return (
    <Stack spacing={3}>
      <Typography variant="h4">Notices</Typography>
      {offers.map((offer) => (
        <RenewalCard key={offer.id} offer={offer} />
      ))}
      {notices.length === 0 && offers.length === 0 && (
        <Typography color="text.secondary">You don&apos;t have any notices.</Typography>
      )}
      {notices.map((notice) => (
        <Paper key={notice.id} variant="outlined" sx={{ p: 3 }}>
          <Stack spacing={1}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
              <Typography variant="h6" sx={{ flexGrow: 1, overflowWrap: 'anywhere' }}>
                {notice.title}
              </Typography>
              <Chip size="small" label={NOTICE_KIND[notice.kind]} />
              <Button size="small" href={`/api/portal/notices/${notice.id}/pdf`}>
                PDF
              </Button>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {notice.sent_at && `Sent ${formatDateTime(notice.sent_at)}`}
              {notice.respond_by && ` · You have until ${formatDate(notice.respond_by)}`}
            </Typography>
            <Typography sx={{ whiteSpace: 'pre-line' }}>{notice.body}</Typography>
          </Stack>
        </Paper>
      ))}
    </Stack>
  );
}
